import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { CURRENT_USER_REGISTERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/registers.tsx';
import { useIssuerIssuedCapitalQuery } from '@/modules/issuer-dashboard/queries/charts.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  ChartEmptyState,
  InfoCard,
  ProgressChart,
  ProgressChartItem,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { ArrowLeftGayIcon, ArrowLeftIcon } from 'ui/icons';
import { goodFormatNumber, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import { GetUserRegistersResponse } from 'api';

type TypeHeadRegister = 'Class' | 'Fund %' | 'Value' | 'Unit / shares on issue' | string;

export const IssuedCapitalProgressChart = () => {
  const { isMobile } = useIsMobile();
  const [page, setPage] = useState<number>(1);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data, isLoading, isFetching, isError } = useIssuerIssuedCapitalQuery({
    page,
    page_size: 5,
  });

  const colors = useMemo(() => ['#1E6091', '#48CAE4', '#56C596', '#F4C430', '#B276E2'], []);

  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY) || -1;
  const registersListIssuer = get(
    queryClient.getQueryData<GetUserRegistersResponse>([CURRENT_USER_REGISTERS_QUERY_KEY]),
    'data',
    [],
  );
  const selectedRegister = registersListIssuer.find(
    item => get(item, 'register_obj.id', 0) === +registerId,
  );

  const selectedRegisterType = get(selectedRegister, 'register_obj.type', null);

  const totalPage = Math.ceil((data?.total || 1) / (data?.page_size || 5));
  const hasNextPage = totalPage > page;
  const totalItems = data?.total || 0;

  const firstItemShownNumber = (page - 1) * 5 + 1;
  const lastItemShownNumber = Math.min((page - 1) * 5 + 5, totalItems);

  const isPossibleGoBack = !isFetching && page > 1;
  const isPossibleGoForward = !isFetching && hasNextPage;

  const goToPreviousPage = useCallback(() => {
    setPage(page => page - 1);
  }, []);

  const goToNextPage = useCallback(() => {
    setPage(page => page + 1);
  }, []);

  const items = useMemo(() => {
    const chartData = data?.data || [];
    return chartData.map<
      ProgressChartItem & { total_units: number; percentage: number; id: number }
    >(({ total, name, total_units, percentage, id }) => ({
      name,
      value: total,
      valueName: '$',
      total_units,
      percentage,
      id,
    }));
  }, [data?.data]);

  const headItems = useMemo<TableHeadColumn<TypeHeadRegister>[]>(() => {
    const items: TableHeadColumn<TypeHeadRegister>[] = [
      { title: 'Class', className: 'min-w-[140px] !py-1.5 text-xs leading-5 text-[#737373]' },
      {
        title: `${capitalize(selectedRegisterType || '')} %`,
        className: '!py-1.5 text-xs leading-5 text-[#737373] min-w-[120px]',
      },
      { title: 'Value', className: '!py-1.5 text-xs leading-5 text-[#737373]' },
      { title: 'Unit / shares on issue', className: '!py-1.5 text-xs leading-5 text-[#737373]' },
    ];

    return items;
  }, [selectedRegisterType]);

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!items?.length) {
      return [];
    }

    return items?.map((data, i) => {
      const body: BodyCell[] = [
        {
          cell: (
            <div className="flex min-w-0 items-center gap-3 font-[500]">
              <span
                className="block w-[6px] h-[6px] min-h-[6px] min-w-[6px] rounded-full"
                style={{ background: colors[i % colors.length] }}
              ></span>
              <span>{data.name}</span>
            </div>
          ),
          className: 'text-sm leading-[22px]',
        },
        { cell: goodFormatNumber(data.percentage) + '%', className: 'text-sm leading-[22px]' },
        {
          cell: goodFormatUSDCurrencyLowPrecision(data.value),
          className: 'text-sm leading-[22px]',
        },
        {
          cell: goodFormatNumber(data.total_units),
          className: 'text-sm leading-[22px]',
        },
      ];

      return {
        cells: body,
        onClick: () =>
          navigate(
            {
              pathname: issuerDashboardPaths.dynamic.classById(String(data.id)),
              search: params.toString(),
            },
            {
              state: { from: pathname + '?' + params.toString() },
            },
          ),
      };
    });
  }, [colors, items, navigate, params, pathname]);

  const bottomContent = !!items.length && (
    <div className="w-full flex flex-row justify-between">
      <div className="text-sm text-neutral-500">
        {firstItemShownNumber} - {lastItemShownNumber} of {totalItems}
      </div>

      <div className="flex flex-row gap-6">
        {isFetching && <Spinner className="w-6 h-6 min-w-[24px] min-h-[24px]" />}
        <button onClick={goToPreviousPage} disabled={!isPossibleGoBack}>
          <img alt="back" src={isPossibleGoBack ? ArrowLeftIcon : ArrowLeftGayIcon} />
        </button>
        <button onClick={goToNextPage} disabled={!isPossibleGoForward}>
          <img
            alt="forward"
            src={isPossibleGoForward ? ArrowLeftIcon : ArrowLeftGayIcon}
            className="rotate-180"
          />
        </button>
      </div>
    </div>
  );

  return (
    <InfoCard
      title="Issued Capital"
      bottomAction={bottomContent}
      classes={{ content: 'p-0 py-6 sm:px-6' }}
    >
      <div className="flex flex-col">
        {!items?.length && (
          <div className="py-4 flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {!isLoading && items?.length > 0 && (
          <>
            <div className="relative sm:px-0 px-6">
              <ProgressChart items={items} colors={colors} />
            </div>
            <div>
              <Table
                className="my-0 rounded-b-xl"
                withBorder={false}
                classNameHead="bg-white border-t-transparent"
                withRounded={false}
                withBorderX={isMobile}
                headItems={headItems}
                items={bodyItems}
                isLoading={isLoading}
                isError={isError}
                mobileFirstCellShowInHeadLeft={true}
              />
            </div>
          </>
        )}
      </div>
    </InfoCard>
  );
};
